import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT } from "@/lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  {
    label: "Finance",
    children: [
      { label: "Loans & Funding", to: "/loans" },
      { label: "Wealth Portfolio", to: "/wealth" },
      { label: "Insurance Solutions", to: "/insurance" },
      { label: "Credit Card Bill Swiping", to: "/credit-card-liquidity" },
    ],
  },
  { label: "Digital Marketing", to: "/digital-marketing" },
  { label: "IT Services", to: "/it-services" },
  { label: "Interiors", to: "/interior-design" },
  {
    label: "Media & Events",
    children: [
      { label: "Videography & Photography", to: "/videography-photography" },
      { label: "Event Management", to: "/event-management" },
      { label: "Destination Weddings", to: "/destination-weddings" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

const ALL_SECTIONS = NAV.flatMap((item) =>
  item.children ? item.children : [item],
);

const testId = (label) =>
  `nav-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 shadow-[0_8px_30px_-18px_rgba(10,25,47,0.35)] backdrop-blur-md"
          : "bg-white/70 backdrop-blur-sm"
      } border-b border-slate-200/80`}
      data-testid="site-header"
    >
      <div className="container-x flex h-[76px] items-center justify-between gap-4">
        <Link to="/" aria-label="Evolve Elite home" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="group relative">
                <button
                  className="flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-sm font-bold text-navy/80 transition hover:bg-slate-100 hover:text-navy"
                  data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
                </button>
                <div className="invisible absolute left-1/2 top-full w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_24px_48px_-16px_rgba(10,25,47,0.25)]">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={({ isActive }) =>
                          `block rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                            isActive
                              ? "bg-gold/15 text-navy"
                              : "text-navy/75 hover:bg-slate-50 hover:text-navy"
                          }`
                        }
                        data-testid={testId(child.label)}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-full px-3 py-2 text-sm font-bold transition ${
                    isActive
                      ? "bg-navy text-white"
                      : "text-navy/80 hover:bg-slate-100 hover:text-navy"
                  }`
                }
                data-testid={testId(item.label)}
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="hidden items-center gap-3 whitespace-nowrap lg:flex">
          <a
            href={CONTACT.phoneHref}
            className="hidden items-center gap-2 text-sm font-bold text-navy transition hover:text-amber-600 2xl:flex"
            data-testid="nav-phone-link"
          >
            <Phone className="h-4 w-4 text-gold-deep" />
            {CONTACT.phoneDisplay}
          </a>
          <Link
            to="/contact"
            className="btn-primary !px-5 !py-2.5"
            data-testid="nav-cta-callback"
          >
            Request Callback
          </Link>
        </div>

        <button
          className="flex items-center rounded-full border border-slate-200 p-2.5 text-navy transition hover:bg-slate-100"
          onClick={() => setOpen(true)}
          aria-label="Open all sections menu"
          data-testid="nav-mobile-open-btn"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-navy/40 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            >
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col overflow-y-auto bg-white p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <Logo compact />
                  <button
                    className="rounded-full border border-slate-200 p-2.5 text-navy"
                    onClick={() => setOpen(false)}
                    aria-label="Close menu"
                    data-testid="nav-mobile-close-btn"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <nav
                  className="mt-8 flex flex-col gap-1"
                  data-testid="nav-all-sections-list"
                >
                  <p className="px-4 pb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-600">
                    All Sections
                  </p>
                  {ALL_SECTIONS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `rounded-xl px-4 py-3 text-base font-bold transition ${
                          isActive
                            ? "bg-navy text-white"
                            : "text-navy hover:bg-slate-50"
                        }`
                      }
                      data-testid={`menu-${testId(item.label)}`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-auto space-y-3 pt-8">
                  <a
                    href={CONTACT.phoneHref}
                    className="btn-outline w-full"
                    data-testid="nav-mobile-phone-link"
                  >
                    <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                  </a>
                  <Link
                    to="/contact"
                    className="btn-gold w-full"
                    data-testid="nav-mobile-cta"
                  >
                    Request Callback
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </header>
  );
}
